<template>
  <div class="uni-aweme-select">
    <!-- 操作区：批量输入 + 刷新（需先选择商品专辑） -->
    <div class="uni-aweme-select__toolbar" style="margin-bottom: 8px">
      <a-button size="small" :disabled="!canOperate" @click="openBatchModal">
        批量输入
      </a-button>
      <a-button
        size="small"
        style="margin-left: 8px"
        :disabled="!canOperate || loading"
        :loading="loading"
        @click="handleRefresh"
      >
        刷新抖音号
      </a-button>
    </div>

    <a-select
      :value="value"
      mode="multiple"
      show-search
      allow-clear
      style="width: 100%"
      :placeholder="placeholder"
      :filter-option="false"
      :loading="loading"
      :not-found-content="notFoundContent"
      @search="handleSearch"
      @change="handleChange"
      @popupScroll="handleScroll"
    >
      <a-select-option v-for="item in options" :key="item.aweme_id" :value="item.aweme_id">
        {{ item.aweme_name ? item.aweme_name + '（' + item.aweme_id + '）' : item.aweme_id }}
      </a-select-option>
      <a-select-option v-if="hasMore" disabled key="__loading_more__">
        {{ loading ? '加载中...' : '下拉加载更多' }}
      </a-select-option>
    </a-select>

    <!-- 批量输入弹窗：每行一个抖音号名称，名称精确匹配 -->
    <a-modal
      title="批量输入抖音号"
      :visible="batchModalVisible"
      :footer="null"
      :mask-closable="false"
      @cancel="closeBatchModal"
    >
      <a-textarea
        v-model="batchInput"
        :rows="8"
        :disabled="matching"
        placeholder="每行输入一个抖音号名称"
      />
      <div style="margin-top: 8px">
        <a-button type="primary" :loading="matching" :disabled="!batchInput.trim()" @click="handleBatchMatch">
          开始匹配
        </a-button>
        <a-button
          size="small"
          style="margin-left: 8px"
          :loading="matching"
          :disabled="matching"
          @click="handleReloadAll"
        >
          重新拉取
        </a-button>
        <span v-if="allAwemes.length" style="margin-left: 8px; color: #999; font-size: 12px">
          当前可投抖音号 {{ allAwemes.length }} 个
        </span>
      </div>

      <!-- 匹配结果：成功绿 / 失败红 -->
      <div v-if="batchResult.length" style="margin-top: 12px; max-height: 220px; overflow-y: auto; border: 1px solid #f0f0f0; border-radius: 4px; padding: 8px">
        <div v-for="(item, index) in batchResult" :key="index" style="margin: 4px 0">
          <span :style="{ color: item.matched ? '#52c41a' : '#ff4d4f' }">
            {{ item.matched ? '✓ ' : '✗ ' }}{{ item.name }}
            <template v-if="item.matched">（{{ item.aweme_id }}）</template>
            <template v-else> 未找到</template>
          </span>
        </div>
      </div>

      <div v-if="batchResult.length" style="margin-top: 12px; text-align: right">
        <a-button @click="closeBatchModal">关闭</a-button>
        <a-button type="primary" style="margin-left: 8px" @click="applyBatchResult">
          加入已选（{{ matchedCount }}）
        </a-button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { getUniAwemes } from '@/api/open'

/**
 * 全域创编可用抖音号选择组件
 *
 * 需选择商品（playletId 专辑ID）后按商品过滤可投抖音号。
 * 支持：下拉滚动加载（分页）+ 抖音ID搜索 + 批量输入名称精确匹配 + 手动刷新。
 * 批量匹配先调后端全量接口拉取完整列表，再按名称精确匹配。
 */
export default {
    name: 'UniAwemeSelect',
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        advertiserId: {
            type: [Number, String],
            default: null,
        },
        playletId: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            options: [],
            page: 1,
            hasMore: false,
            loading: false,
            searchKeyword: '',
            selectedItems: [],
            // 批量输入弹窗状态
            batchModalVisible: false,
            batchInput: '',
            matching: false,
            allAwemes: [],
            batchResult: [],
        }
    },
    computed: {
        placeholder() {
            if (!this.playletId) {
                return '请先选择商品'
            }
            return '请输入抖音ID搜索，或下拉选择'
        },
        notFoundContent() {
            if (!this.playletId) {
                return '请先选择商品'
            }
            return this.loading ? '加载中...' : '暂无可用抖音号'
        },
        canOperate() {
            return !!this.advertiserId && !!this.playletId
        },
        matchedCount() {
            return this.batchResult.filter(item => item.matched).length
        },
    },
    watch: {
        advertiserId() {
            this.reset()
        },
        playletId() {
            this.reset()
        },
    },
    mounted() {
        this.fetch(1)
    },
    methods: {
        reset() {
            this.options = []
            this.selectedItems = []
            this.page = 1
            this.hasMore = false
            this.searchKeyword = ''
            // 切换商品/广告主时清空已选
            if (this.value && this.value.length > 0) {
                this.$emit('change', [], [])
            }
            this.fetch(1)
        },
        fetch(page = 1, keyword = this.searchKeyword) {
            if (!this.advertiserId || !this.playletId) {
                this.options = []
                this.hasMore = false
                return Promise.resolve()
            }
            this.loading = true
            return getUniAwemes({
                advertiser_id: this.advertiserId,
                playlet_id: this.playletId,
                page: page,
                page_size: 50,
                search_keyword: keyword || undefined,
            }).then(res => {
                const list = res.data.list || []
                this.page = page
                this.hasMore = !!res.data.has_more
                if (page === 1) {
                    this.options = list
                } else {
                    const seen = {}
                    const merged = this.options.concat(list).filter(item => {
                        if (seen[item.aweme_id]) return false
                        seen[item.aweme_id] = true
                        return true
                    })
                    this.options = merged
                }
                // 同步已选抖音号的最新名称
                const valueMap = {}
                this.options.forEach(item => { valueMap[item.aweme_id] = item.aweme_name })
                this.selectedItems = (this.value || []).map(id => ({
                    id: id,
                    name: valueMap[id] || '',
                }))
            }).finally(() => {
                this.loading = false
            })
        },
        handleSearch(value) {
            this.searchKeyword = value.trim()
            if (this.searchTimer) {
                clearTimeout(this.searchTimer)
            }
            this.searchTimer = setTimeout(() => {
                this.fetch(1, this.searchKeyword)
            }, 300)
        },
        handleScroll(e) {
            const el = e.target
            if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
                this.loadMore()
            }
        },
        loadMore() {
            if (this.hasMore && !this.loading) {
                this.fetch(this.page + 1)
            }
        },
        handleChange(val) {
            const valueMap = {}
            this.options.forEach(item => { valueMap[item.aweme_id] = item.aweme_name })
            this.selectedItems = (val || []).map(id => ({
                id: id,
                name: valueMap[id] || '',
            }))
            this.$emit('change', val || [], this.selectedItems)
        },
        // 手动刷新：重新拉取下拉列表并同步已选名称
        handleRefresh() {
            this.searchKeyword = ''
            this.fetch(1, '')
        },
        // 打开批量输入弹窗，拉取全量可投抖音号
        openBatchModal() {
            if (!this.canOperate) {
                return
            }
            this.batchModalVisible = true
            this.batchInput = ''
            this.batchResult = []
            this.allAwemes = []
            this.fetchAllAwemes()
        },
        // 拉取全量可投抖音号（all=true，后端循环分页全量返回）
        fetchAllAwemes() {
            if (!this.canOperate) {
                return Promise.resolve()
            }
            this.matching = true
            return getUniAwemes({
                advertiser_id: this.advertiserId,
                playlet_id: this.playletId,
                all: true,
                page_size: 100,
            }).then(res => {
                this.allAwemes = res.data.list || []
            }).catch(() => {
                this.allAwemes = []
            }).finally(() => {
                this.matching = false
            })
        },
        // 重新拉取全量列表
        handleReloadAll() {
            this.fetchAllAwemes()
        },
        // 按名称精确匹配当前输入
        handleBatchMatch() {
            if (!this.allAwemes.length) {
                // 全量列表未就绪时先拉取
                this.fetchAllAwemes().then(() => this.doMatch())
                return
            }
            this.doMatch()
        },
        doMatch() {
            // 名称 -> 抖音号映射（名称精确匹配）
            const nameMap = {}
            this.allAwemes.forEach(item => {
                if (!nameMap[item.aweme_name]) {
                    nameMap[item.aweme_name] = item.aweme_id
                }
            })
            // 每行 trim 后精确匹配
            this.batchResult = this.batchInput.split('\n').map(line => line.trim()).filter(Boolean).map(line => {
                const awemeId = nameMap[line]
                return {
                    name: line,
                    matched: !!awemeId,
                    aweme_id: awemeId || '',
                }
            })
        },
        // 应用匹配成功的抖音号，失败项留在弹窗可修改重输
        applyBatchResult() {
            const matchedItems = this.batchResult.filter(item => item.matched)
            if (!matchedItems.length) {
                return
            }
            const valueMap = {}
            this.allAwemes.forEach(item => { valueMap[item.aweme_id] = item.aweme_name })
            // 与已选去重合并
            const newIds = Array.from(new Set((this.value || []).concat(matchedItems.map(item => item.aweme_id))))
            this.selectedItems = newIds.map(id => ({
                id: id,
                name: valueMap[id] || '',
            }))
            this.$emit('change', newIds, this.selectedItems)
            // 失败项保留在弹窗，输入框更新为未匹配行便于重输
            const failedLines = this.batchResult.filter(item => !item.matched).map(item => item.name)
            this.batchInput = failedLines.join('\n')
            this.batchResult = this.batchResult.filter(item => !item.matched)
            if (failedLines.length === 0) {
                this.closeBatchModal()
            }
        },
        closeBatchModal() {
            this.batchModalVisible = false
        },
    },
}
</script>
